import React, { useEffect, useRef, useCallback, FC } from "react";
import GoogleMapReact from "google-map-react";
import map from "lodash/map";
import forEach from "lodash/forEach";
import MapAreaDescription from "./mapAreaDescription";
import { ENV_CONFIG } from "../../../config/env";
import { useLanguage } from "@/hooks";
import "./style.scss";

type DescriptionCmp = React.ComponentType<any>;

const GoogleMap: FC<{
  stores: any;
  selectedStore: any;
  lat: number;
  lng: number;
  zoom: number;
  onDescriptionClick?: any;
  mapContainerHeight?: string;
  customIcon?: boolean;
  onMapBoundsMove?: any;
  recalculationTrigger?: any;
  DescriptionComponent?: DescriptionCmp;
}> = ({
  stores,
  selectedStore,
  lat,
  lng,
  zoom,
  onDescriptionClick,
  mapContainerHeight,
  customIcon = false,
  onMapBoundsMove,
  recalculationTrigger,
  DescriptionComponent = MapAreaDescription
}) => {
  const language = useLanguage();
  const mapInstanceRef = useRef(null);

  const apiIsLoaded = useCallback((map, maps, places) => {
    if (!places.length) return;
    const bounds = getMapBounds(map, maps, places);
    recalculateFitting(map, bounds);
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = { map, maps };
    }
  }, []);

  useEffect(() => {
    if (mapInstanceRef.current) {
      const { map, maps } = mapInstanceRef.current;
      const bounds = getMapBounds(map, maps, stores);
      recalculateFitting(map, bounds);
    }
  }, [recalculationTrigger]);

  return (
    <div style={{ height: mapContainerHeight || "500px", width: "100%" }}>
      {stores && (
        <GoogleMapReact
          yesIWantToUseGoogleMapApiInternals
          bootstrapURLKeys={{ key: ENV_CONFIG.googleMap, language }}
          center={{ lat, lng }}
          defaultZoom={zoom || 150}
          onGoogleApiLoaded={({ map, maps }) => apiIsLoaded(map, maps, stores)}
          onChange={onMapBoundsMove}
          options={{
            disableDefaultUI: true,
            gestureHandling: "greedy",
            zoomControl: false
          }}
        >
          {map(stores, (place, index) => {
            const areaDescriptionProps = {
              key: place.id || place.station_id || index,
              showCustomIcon: customIcon,
              selected:
                selectedStore &&
                [
                  selectedStore.collectionPointId,
                  selectedStore.station_id
                ].find(
                  id =>
                    id === place.station_id || id === place.collectionPointId
                ),
              address: place.address?.label || place.address_info,
              name: place.station_name?.label || place.station_name,
              phone: place.phone,
              extraInfo: place.extra_info,
              place,
              onDescriptionClick,
              mapIconURL: place.mapIconURL,
              lat: place.lat,
              lng: place.lng,
              workingHours: place.workingHours
            };

            return <DescriptionComponent {...areaDescriptionProps} />;
          })}
        </GoogleMapReact>
      )}
    </div>
  );
};

let orientation;

const getMapBounds = (map, maps, places) => {
  const bounds = new maps.LatLngBounds();
  forEach(places, place => {
    bounds.extend(new maps.LatLng(place.lat, place.lng));
  });
  return bounds;
};

const recalculateFitting = (map, bounds) => {
  map.fitBounds(bounds);
  if (map.getZoom() > 14) {
    map.setZoom(14);
  }
};

export default GoogleMap;
